/* ============================================================
   HYDRO PLANT — Product sticky in-page nav
   - Builds pills from section blocks (label + target selector),
     or auto-discovers elements marked with [data-hp-nav="Label"].
   - Pins with JS (position:fixed) so it stays put across sibling
     content sections, tucked directly under the sticky header
     (follows the header's reveal/hide), then releases at the end.
   - Smooth-scrolls on click and highlights the section in view.
   ============================================================ */
(function () {
  var HEADER_GAP = 6; // px between the site header and the bar when pinned

  function throttle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { fn(); ticking = false; });
    };
  }

  function StickyNav(root) {
    this.root = root;
    this.bar = root.querySelector('.hp-snav');
    this.track = root.querySelector('.hp-snav__track');
    this.sentinel = root.querySelector('.hp-snav__sentinel');
    if (!this.bar || !this.track) return;

    this.items = this.collectItems();
    if (!this.items.length) { root.style.display = 'none'; return; }

    this.stickyTop = parseInt(getComputedStyle(root).getPropertyValue('--hp-snav-top'), 10) || 90;
    this.header = document.querySelector('.m-header__wrapper') || document.querySelector('.m-header');
    this.fixed = false;

    // Spacer keeps the page from jumping when the bar leaves normal flow.
    this.spacer = document.createElement('span');
    this.spacer.className = 'hp-snav__spacer';
    this.bar.parentNode.insertBefore(this.spacer, this.bar.nextSibling);

    this.bindClicks();
    var handler = throttle(this.onScroll.bind(this));
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    this.onScroll();
  }

  StickyNav.prototype.resolve = function (selector) {
    if (!selector) return null;
    try { return document.querySelector(selector); } catch (e) { return null; }
  };

  StickyNav.prototype.collectItems = function () {
    var self = this;
    var out = [];
    var pills = Array.prototype.slice.call(this.track.querySelectorAll('.hp-snav__item'));
    pills.forEach(function (btn) {
      var target = self.resolve(btn.getAttribute('data-target'));
      if (target) out.push({ btn: btn, target: target });
    });
    if (!out.length && this.root.getAttribute('data-hp-auto') === 'true') {
      var found = Array.prototype.slice.call(document.querySelectorAll('[data-hp-nav]'));
      found.forEach(function (el) {
        var label = el.getAttribute('data-hp-nav') || (el.querySelector('h1,h2,h3,h4') || {}).textContent || '';
        label = (label || '').trim();
        if (!label) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hp-snav__item';
        btn.textContent = label;
        self.track.insertBefore(btn, self.track.querySelector('.hp-snav__top') || null);
        out.push({ btn: btn, target: el });
      });
    }
    return out;
  };

  // Where the bar pins: directly under the visible sticky header if there is
  // one; otherwise the section's configured "sticky distance from top".
  StickyNav.prototype.topFor = function () {
    if (this.header) {
      var cs = getComputedStyle(this.header);
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        return Math.max(0, this.header.getBoundingClientRect().bottom) + HEADER_GAP;
      }
    }
    return this.stickyTop;
  };

  StickyNav.prototype.scrollOffset = function () {
    return this.topFor() + this.track.offsetHeight + 16;
  };

  StickyNav.prototype.bindClicks = function () {
    var self = this;
    this.items.forEach(function (it) {
      it.btn.addEventListener('click', function (e) {
        e.preventDefault();
        var y = window.pageYOffset + it.target.getBoundingClientRect().top - self.scrollOffset();
        window.scrollTo({ top: y, behavior: 'smooth' });
      });
    });
    var topBtn = this.root.querySelector('.hp-snav__top');
    if (topBtn) {
      topBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var main = document.querySelector('.main-product') || document.body;
        var y = Math.max(0, window.pageYOffset + main.getBoundingClientRect().top - self.scrollOffset());
        window.scrollTo({ top: y, behavior: 'smooth' });
      });
    }
  };

  StickyNav.prototype.setFixed = function (on) {
    if (on === this.fixed) return;
    this.fixed = on;
    if (on) {
      this.spacer.style.height = this.bar.offsetHeight + 'px';
      this.bar.classList.add('is-fixed');
    } else {
      this.spacer.style.height = '0px';
      this.bar.classList.remove('is-fixed');
      this.bar.style.top = '';
    }
  };

  StickyNav.prototype.setActive = function (activeBtn) {
    this.items.forEach(function (it) {
      it.btn.classList.toggle('is-active', it.btn === activeBtn);
    });
    if (activeBtn && this.track.scrollWidth > this.track.clientWidth) {
      var br = activeBtn.getBoundingClientRect();
      var tr = this.track.getBoundingClientRect();
      if (br.left < tr.left || br.right > tr.right) {
        this.track.scrollLeft += br.left - tr.left - 12;
      }
    }
  };

  StickyNav.prototype.onScroll = function () {
    var trackH = this.track.offsetHeight;
    var effTop = this.topFor();
    var sentinelTop = this.sentinel.getBoundingClientRect().top;
    var lastEl = this.items[this.items.length - 1].target;
    var endBottom = lastEl.getBoundingClientRect().bottom;

    // Pin once the bar's home reaches the pin line, release once the
    // last section has scrolled (mostly) past it.
    var shouldFix = sentinelTop <= effTop && endBottom > (effTop + trackH * 0.5);
    this.setFixed(shouldFix);
    if (this.fixed) this.bar.style.top = effTop + 'px';

    // Scroll-spy
    var line = effTop + trackH + 20;
    var current = this.items[0];
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].target.getBoundingClientRect().top - line <= 0) current = this.items[i];
      else break;
    }
    this.setActive(current ? current.btn : null);
  };

  function init() {
    document.querySelectorAll('[data-hp-snav-root]').forEach(function (root) {
      if (root.__hpSnav) return;
      root.__hpSnav = true;
      new StickyNav(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
